/** Export jobs context (TZ 8.9.2): shape, hook and download helpers shared by the provider and its consumers. */
import { createContext, useContext } from 'react';
import type { ExportJob } from '../../api/api_types';
import { exportDownloadPath } from '../../api/client_endpoints';
import type { ExportOrder } from './export_filters';

export interface TrackedExportJob {
  job: ExportJob;
  label: string;
  startedAt: number;
}

/** What a download needs: the export id and its link (an `ExportJob` or an `ExportListItem`). */
export type DownloadableExport = Pick<ExportJob, 'id' | 'download_url'>;

export interface ExportJobsContextValue {
  jobs: readonly TrackedExportJob[];
  startExport: (exportOrder: ExportOrder, label: string) => Promise<void>;
  dismissJob: (jobId: string) => void;
  /**
   * Starts the download and takes the export off the lists: the server removes it once the file was streamed
   * completely (owner decision 2026-10-05); the lists refresh a moment later from the server.
   */
  downloadExport: (exportJob: DownloadableExport, fileName: string) => void;
  /** Exports downloaded in this session: hidden until the server list confirms they are gone. */
  downloadedExportIds: ReadonlySet<string>;
}

export const ExportJobsContext = createContext<ExportJobsContextValue | null>(null);

export function exportDownloadUrl(exportJob: DownloadableExport): string {
  return exportJob.download_url ?? exportDownloadPath(exportJob.id);
}

export function openDownload(downloadUrl: string, fileName: string): void {
  const linkElement = document.createElement('a');
  linkElement.href = downloadUrl;
  linkElement.download = fileName;
  linkElement.rel = 'noopener';
  document.body.append(linkElement);
  linkElement.click();
  linkElement.remove();
}

export function isFinishedExport(exportJob: ExportJob): boolean {
  return exportJob.status === 'done' || exportJob.status === 'failed';
}

export class MissingExportJobsProviderError extends Error {
  constructor() {
    super('useExportJobs must be used inside <ExportJobsProvider>');
    this.name = 'MissingExportJobsProviderError';
  }
}

export function useExportJobs(): ExportJobsContextValue {
  const contextValue = useContext(ExportJobsContext);
  if (contextValue === null) {
    throw new MissingExportJobsProviderError();
  }
  return contextValue;
}
